import { AspectInfoDto, Aspects, NatalChartResultDto, PlanetInfoDto, Planets, Zodiac } from "../../../api";

export type ChartPlanet = {
  name: Planets;
  sign: Zodiac | undefined;
  degree: number;
  house?: number;
  forecast?: string | undefined;
};

export type ChartAnglePoint = {
  sign: Zodiac | undefined;
  degree: number;
  forecast?: string | undefined;
};

export type ChartHousePlacement = {
  number: number;
  sign: Zodiac | undefined;
  cuspDegree: number;
};

export type ChartData = {
  sun: { sign: Zodiac | undefined; house: number; degree: number; forecast?: string };
  moon: { sign: Zodiac | undefined; house: number; degree: number; forecast?: string };
  ascendant: ChartAnglePoint;
  descendant: ChartAnglePoint;
  mediumCoeli: ChartAnglePoint;
  imumCoeli: ChartAnglePoint;
  planets: ChartPlanet[];
  houses: ChartHousePlacement[];
  globalForecast?: string | undefined;
};

export type AspectUI = {
  from: Planets;
  to: Planets;
  type: Aspects;
  orb: number;
  nature: "harmonia" | "desafio";
  strength: number;
};

export function natalChartToUI(res: NatalChartResultDto): { chartData: ChartData; aspects: AspectUI[] } {
  const planets = res.planets ?? [];

  const findPlanet = (planet: Planets) =>
    planets.find((p: PlanetInfoDto) => p.planet === planet);

  const sun = findPlanet(Planets.Sun);
  const moon = findPlanet(Planets.Moon);

  const basicPlacement = (p?: PlanetInfoDto) => ({
    sign: p?.sign,
    house: p?.houseNumber ?? 0,
    degree: p?.degree ?? 0,
    forecast: p?.forecast,
  });

  const otherPlanets: ChartPlanet[] = planets
    .filter((p) => p.planet && p.planet !== Planets.Sun && p.planet !== Planets.Moon)
    .map((p) => ({
      name: p.planet as Planets,
      sign: p.sign,
      degree: p.degree ?? 0,
      house: p.houseNumber ?? 0,
      forecast: p.forecast,
    }));

  const houses: ChartHousePlacement[] = (res.houses ?? [])
    .map((house, idx) => {
      const number = house?.number ?? idx + 1;
      const cuspDegree = typeof house?.cuspDegree === "number" ? house.cuspDegree : 0;
      return { number, sign: house?.sign, cuspDegree };
    })
    .filter((h) => h.number >= 1 && h.number <= 12)
    .sort((a, b) => a.number - b.number);

  const harmonious = [Aspects.Trine, Aspects.Sextile, Aspects.Conjunction];
  const aspects: AspectUI[] = (res.aspects ?? []).map((a: AspectInfoDto) => {
    const type = a.aspect ?? Aspects.None;
    return {
      from: a.planet1!,
      to: a.planet2!,
      type,
      orb: a.orb ?? 0,
      nature: harmonious.includes(type) ? "harmonia" : "desafio",
      strength: a.strength ?? 1,
    };
  });

  const chartData: ChartData = {
    sun: basicPlacement(sun),
    moon: basicPlacement(moon),
    ascendant: { sign: res.ascendantSign, degree: res.ascendantDegree!, forecast: res.ascendantForecast },
    descendant: { sign: res.descendantSign, degree: res.descendantDegree!, forecast: res.descendantForecast },
    imumCoeli: { sign: res.imumCoeliSign, degree: res.imumCoeliDegree!, forecast: res.imumCoeliForecast },
    mediumCoeli: { sign: res.mediumCoeliSign, degree: res.mediumCoeliDegree!, forecast: res.mediumCoeliForecast },
    planets: otherPlanets,
    houses,
    globalForecast: res.globalForecast,
  };

  return { chartData, aspects };
}
